import { useState } from "react";
import { toast } from "sonner";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { EmptyState } from "@/components/shared/EmptyState";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AssignPlanDialog } from "@/components/subscriptions/AssignPlanDialog";
import { useOwnerSubscription } from "@/hooks/useOwners";
import {
  useCancelSubscription,
  useReactivateSubscription,
} from "@/hooks/useSubscriptions";
import { formatDate } from "@/utils/format";

interface Props {
  ownerId?: string;
}

const Field = ({ label, value }: { label: string; value?: string | null }) => (
  <div>
    <dt className="text-xs text-muted-foreground">{label}</dt>
    <dd className="text-sm mt-0.5">{value || "—"}</dd>
  </div>
);

const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="bg-card rounded-card border border-border p-4 space-y-3">
    <h4 className="font-semibold text-sm">{title}</h4>
    <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">{children}</dl>
  </div>
);

const CANCELLABLE = new Set(["ACTIVE", "TRIALING", "PAST_DUE", "GRACE_PERIOD"]);
const REACTIVATABLE = new Set(["CANCELLED", "EXPIRED", "SUSPENDED"]);
const ASSIGNABLE = new Set([
  "TRIALING",
  "EXPIRED",
  "CANCELLED",
  "PAST_DUE",
  "SUSPENDED",
]);

export const SubscriptionTab = ({ ownerId }: Props) => {
  const { data: sub, isLoading, error } = useOwnerSubscription(ownerId);
  const cancel = useCancelSubscription(ownerId);
  const reactivate = useReactivateSubscription(ownerId);
  const [assignOpen, setAssignOpen] = useState(false);

  if (!ownerId) return <EmptyState title="No owner linked to this business." />;

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-28 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-destructive p-4 rounded-card border border-destructive/30 bg-destructive/5">
        Failed to load subscription: {(error as Error).message}
      </div>
    );
  }

  if (!sub) {
    return (
      <>
        <EmptyState
          title="No active subscription"
          description="This owner has no active subscription."
          action={
            ownerId ? (
              <Button size="sm" onClick={() => setAssignOpen(true)}>
                Assign plan
              </Button>
            ) : undefined
          }
        />
        {ownerId && (
          <AssignPlanDialog
            open={assignOpen}
            onOpenChange={setAssignOpen}
            ownerId={ownerId}
          />
        )}
      </>
    );
  }

  const billingLabel =
    sub.billingCycle === "YEARLY"
      ? "Yearly"
      : sub.billingCycle === "MONTHLY"
        ? "Monthly"
        : (sub.billingCycle ?? "—");

  const unitDisplay =
    sub.unitAmount != null
      ? `${sub.currency ?? ""} ${Number(sub.unitAmount).toLocaleString()}`.trim()
      : null;

  const hasTrial = !!(sub.trialStartAt || sub.trialEndAt);
  const hasCancelInfo = !!(
    sub.cancelAtPeriodEnd ||
    sub.cancelledAt ||
    sub.gracePeriodEnd
  );

  const onCancel = () => {
    cancel.mutate(
      { id: sub.id },
      {
        onSuccess: () => toast.success("Subscription cancelled"),
        onError: (e) => toast.error(e.message),
      },
    );
  };

  const onReactivate = () => {
    reactivate.mutate(
      { id: sub.id },
      {
        onSuccess: () => toast.success("Subscription reactivated"),
        onError: (e) => toast.error(e.message),
      },
    );
  };

  return (
    <div className="space-y-4">
      <SectionCard title="Plan & Status">
        <div className="col-span-full flex items-center gap-3 flex-wrap">
          <span className="font-medium">
            {sub.plan?.name ??
              (sub.isCustom ? "Custom Subscription" : "Custom")}
          </span>
          <StatusBadge status={sub.status} />
        </div>
        <Field label="Billing cycle" value={billingLabel} />
        <Field label="Gateway" value={sub.gateway ?? null} />
        {sub.plan?.maxBusinesses != null && (
          <Field
            label="Max businesses"
            value={String(sub.plan.maxBusinesses)}
          />
        )}
        {sub.plan?.priceMonthly != null && (
          <Field
            label="Monthly price"
            value={`₹${sub.plan.priceMonthly.toLocaleString()}`}
          />
        )}
        {unitDisplay ? (
          <Field label="Billed amount" value={unitDisplay} />
        ) : null}
      </SectionCard>

      <SectionCard title="Billing Period">
        <Field
          label="Started"
          value={sub.startedAt ? formatDate(sub.startedAt) : null}
        />
        <Field
          label="Period start"
          value={formatDate(sub.currentPeriodStart)}
        />
        <Field label="Period end" value={formatDate(sub.currentPeriodEnd)} />
      </SectionCard>

      {hasTrial && (
        <SectionCard title="Trial">
          <Field
            label="Trial start"
            value={sub.trialStartAt ? formatDate(sub.trialStartAt) : null}
          />
          <Field
            label="Trial end"
            value={sub.trialEndAt ? formatDate(sub.trialEndAt) : null}
          />
        </SectionCard>
      )}

      {hasCancelInfo && (
        <SectionCard title="Cancellation">
          {sub.gracePeriodEnd ? (
            <Field
              label="Grace period ends"
              value={formatDate(sub.gracePeriodEnd)}
            />
          ) : null}
          {sub.cancelAtPeriodEnd ? (
            <div className="col-span-full text-xs text-amber-600 dark:text-amber-400 font-medium">
              Scheduled to cancel at period end
            </div>
          ) : null}
          {sub.cancelledAt ? (
            <Field label="Cancelled at" value={formatDate(sub.cancelledAt)} />
          ) : null}
          {sub.cancelReason ? (
            <div className="col-span-full">
              <dt className="text-xs text-muted-foreground">Cancel reason</dt>
              <dd className="text-sm mt-0.5">{sub.cancelReason}</dd>
            </div>
          ) : null}
        </SectionCard>
      )}

      <div className="flex gap-2 flex-wrap">
        {CANCELLABLE.has(sub.status) && (
          <Button
            variant="destructive"
            size="sm"
            onClick={onCancel}
            disabled={cancel.isPending}
          >
            Cancel subscription
          </Button>
        )}
        {REACTIVATABLE.has(sub.status) && (
          <Button
            variant="outline"
            size="sm"
            onClick={onReactivate}
            disabled={reactivate.isPending}
          >
            Reactivate subscription
          </Button>
        )}
        {ASSIGNABLE.has(sub.status) && ownerId && (
          <Button size="sm" onClick={() => setAssignOpen(true)}>
            Assign plan
          </Button>
        )}
      </div>

      {ownerId && (
        <AssignPlanDialog
          open={assignOpen}
          onOpenChange={setAssignOpen}
          ownerId={ownerId}
        />
      )}
    </div>
  );
};

export default SubscriptionTab;
